import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCreateProject, useProject } from '../../hooks/useProject';
import { useAuthProvider } from '../../auth/hooks/auth-provider';
import ProjectForm from '../../components/project/ProjectForm';
import { Project, ProjectCreateRequest, ProjectStatus } from '../../models/Project';
import { UserType } from '../../models/UserType';

const ProjectCreate: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthProvider();
  const [searchParams] = useSearchParams();

  // Check if duplicating from another project
  const duplicateFromId = searchParams.get('duplicateFrom');
  const duplicateProjectId = duplicateFromId ? parseInt(duplicateFromId, 10) : 0;

  // Fetch the source project if duplicating
  const { data: sourceProject, isLoading: isLoadingSource } = useProject(duplicateProjectId);

  const createProjectMutation = useCreateProject();

  // Alert state
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState<'success' | 'error'>('success');

  // Build initial data for duplication
  const duplicateInitialData = useMemo((): Partial<Project> | undefined => {
    if (!sourceProject || !duplicateFromId) return undefined;

    // Get the copy prefix based on language
    const copyPrefix = i18n.language === 'fr' ? 'COPIE' : 'COPY';

    return {
      // Pre-filled fields with COPIE/COPY prefix for name
      name: `${copyPrefix} ${sourceProject.name}`,
      companyId: sourceProject.companyId,
      company: sourceProject.company,
      domain: sourceProject.domain,
      missionType: sourceProject.missionType,
      objectives: sourceProject.objectives,
      presentationAndNeeds: sourceProject.presentationAndNeeds,
      talentHardSkills: sourceProject.talentHardSkills,
      talentSoftSkills: sourceProject.talentSoftSkills,
      // All other fields are left empty (not copied)
    };
  }, [sourceProject, duplicateFromId, i18n.language]);
  
  const handleCreateProject = async (projectData: ProjectCreateRequest, isDraft: boolean = false) => {
    try {
      // If projectData has an id, it means a draft was already created and patched by ProjectForm
      // We just need to show success message and navigate
      if ((projectData as any).id) {
        const projectId = (projectData as any).id;
        setAlertSeverity('success');
        setAlertMessage(isDraft ? t('project.savedAsDraft') : t('project.createdSuccessfully'));
        setAlertOpen(true);

        setTimeout(() => {
          navigate(`/projects/${projectId}`);
        }, 1500);
        return;
      }

      // No draft exists yet, create a new project
      const dataToSubmit = {
        ...projectData,
        status: isDraft ? ProjectStatus.Draft : ProjectStatus.Submitted
      };

      const createdProject = await createProjectMutation.mutateAsync(dataToSubmit);

      setAlertSeverity('success');
      setAlertMessage(isDraft ? t('project.savedAsDraft') : t('project.createdSuccessfully'));
      setAlertOpen(true);

      // Always navigate to project details after creation
      setTimeout(() => {
        navigate(`/projects/${createdProject.id}`);
      }, 1500);
    } catch (error) {
      console.error('Error creating project:', error);
      setAlertSeverity('error');
      setAlertMessage(t('project.creationError'));
      setAlertOpen(true);
    }
  };
  
  const handleAlertClose = () => {
    setAlertOpen(false);
  };
  
  const handleNavigateToList = () => {
    if (user.type === UserType.Client) {
      navigate('/projects');
    } else {
      navigate('/projects/all');
    }
  };
  
  // Show loading state while fetching source project for duplication
  if (duplicateFromId && isLoadingSource) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  const pageTitle = duplicateFromId
    ? t('project.duplicate.createFromDuplicate')
    : t('project.createProject');

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h2">{pageTitle}</Typography>
      </Box>

      <Breadcrumbs separator="›" aria-label="breadcrumb" sx={{ mb: 3 }}>
        <Link
          underline="hover"
          color="inherit"
          onClick={handleNavigateToList}
          sx={{ cursor: 'pointer' }}
        >
          {user.type === UserType.Client ? t('project.myProjects') : t('project.projectList')}
        </Link>
        <Typography color="text.primary">{pageTitle}</Typography>
      </Breadcrumbs>

      {duplicateFromId && sourceProject && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {t('project.duplicate.duplicatingFrom', { projectName: sourceProject.name })}
        </Alert>
      )}

      <ProjectForm
        initialData={duplicateInitialData}
        onSubmit={handleCreateProject}
        isSubmitting={createProjectMutation.isPending}
        submitButtonText={t('project.create')}
      />
      
      <Snackbar
        open={alertOpen}
        autoHideDuration={5000}
        onClose={handleAlertClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleAlertClose}
          severity={alertSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProjectCreate;